import React, {useCallback, useMemo, useState} from "react";
import {Select} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {TICKET_LIST_STATES, TICKET_SCOPES, TICKET_STATUSES_BY_STATE} from "constants/ticket";
import {DebouncedInput} from "../common/DebouncedInput";
import {TicketDetailModal} from "../detail/TicketDetailModal";
import {CustomerTicketsTable} from "./CustomerTicketsTable";
import {FiltersRow} from "./CustomerTickets.styles";

const defaultFilters = {number: "", status: "", skip: 0, limit: 20};

const withoutEmptyValues = filters =>
    Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ""));

export const CustomerTicketList = ({state, subCompanyId, refreshKey, onChanged}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const [filters, setFilters] = useState(defaultFilters);
    const [detailId, setDetailId] = useState(null);

    const handleNumberChange = useCallback(
        number => setFilters(previous => ({...previous, number, skip: 0})),
        []
    );

    const queryParams = useMemo(
        () => ({
            scope: TICKET_SCOPES.customer,
            subCompanyId,
            state,
            ...withoutEmptyValues(filters)
        }),
        [subCompanyId, state, filters]
    );

    const [loading, tickets] = useFetch({
        fetchFn: stores.ticketStore.getTickets.bind(null, queryParams),
        defaultDataValue: {},
        dependencies: [JSON.stringify(queryParams), refreshKey]
    });

    return (
        <>
            <FiltersRow>
                <DebouncedInput
                    value={filters.number}
                    placeholder={t("list.filters.number", {ns: "tickets"})}
                    onChange={handleNumberChange}
                />
                <Select
                    allowClear
                    placeholder={t("list.filters.allStatuses", {ns: "tickets"})}
                    value={filters.status || undefined}
                    onChange={status =>
                        setFilters(previous => ({...previous, status: status || "", skip: 0}))
                    }
                >
                    {TICKET_STATUSES_BY_STATE[state].map(status => (
                        <Select.Option key={status} value={status}>
                            {t(`statuses.${status}`, {ns: "tickets"})}
                        </Select.Option>
                    ))}
                </Select>
            </FiltersRow>
            <CustomerTicketsTable
                rows={tickets?.data?.rows || []}
                total={tickets?.data?.dataCount || 0}
                loading={loading}
                pagination={filters}
                paginated={state !== TICKET_LIST_STATES.closed}
                onView={record => setDetailId(record._id)}
                onPageChange={(page, pageSize) =>
                    setFilters(previous => ({
                        ...previous,
                        skip: (page - 1) * pageSize,
                        limit: pageSize
                    }))
                }
            />
            <TicketDetailModal
                ticketId={detailId}
                customerView
                readOnly
                roleOptions={[]}
                roleLabels={{}}
                onClose={() => setDetailId(null)}
                onChanged={onChanged}
            />
        </>
    );
};
